/** Confirms deleting a project. It takes the project's runs and files with it and cannot
 * be undone, so the operator types a phrase rather than clicking through a prompt. */
import { useEffect, useRef, useState } from "react";

import styles from "./DeleteProjectDialog.module.css";

const PHRASE = "delete this project";

export const DeleteProjectDialog = ({
  open,
  projectName,
  deleting,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  projectName: string;
  deleting: boolean;
  /** The failed delete's message, shown in the dialog so the operator can retry. */
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const [typed, setTyped] = useState("");
  const matches = typed.trim().toLowerCase() === PHRASE;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setTyped("");
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onCancel={(event) => {
        event.preventDefault();
        if (!deleting) onCancel();
      }}
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (matches && !deleting) onConfirm();
        }}
      >
        <h2 className={styles.heading}>Delete project</h2>
        <p className={styles.name}>{projectName}</p>
        <p className={styles.help}>
          This deletes the project, all of its agent runs and conversations, and every attached
          file. It cannot be undone.
        </p>
        <label className={styles.field}>
          <span>
            Type <strong>{PHRASE}</strong> to confirm
          </span>
          <input
            className={styles.input}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            disabled={deleting}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
        </label>
        {error && <p className={styles.error}>Couldn't delete project: {error}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={onCancel} disabled={deleting}>
            Cancel
          </button>
          <button type="submit" className={styles.delete} disabled={!matches || deleting}>
            {deleting ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </form>
    </dialog>
  );
};
